// 🔐 Serviço de Autenticação
// Sistema de login baseado em magic link (hub_login)

import type { Cliente, SessaoBase } from '../types/auth'

export interface LoginResponse {
  success: boolean
  cliente?: Cliente
  error?: string
}

/**
 * 🔐 Classe para gerenciar autenticação de clientes via magic link
 * Responsável por validar e persistir sessões locais
 */
export class AuthService {
  private static readonly STORAGE_KEY = 'sabhia-auth-cliente'
  private static readonly SESSION_DURATION = 30 * 24 * 60 * 60 * 1000 // 30 dias

  /**
   * 🔍 Verificar sessão atual
   * Verifica se há uma sessão válida salva
   */
  static verificarSessao(): SessaoBase {
    try {
      const sessaoSalva = localStorage.getItem(this.STORAGE_KEY)
      
      if (!sessaoSalva) {
        return {
          isAuthenticated: false,
          cliente: null,
          loading: false,
          error: null
        }
      }

      const { cliente, timestamp } = JSON.parse(sessaoSalva)
      
      // Verificar se a sessão não expirou
      if (Date.now() - timestamp > this.SESSION_DURATION) {
        this.logout()
        return {
          isAuthenticated: false,
          cliente: null,
          loading: false,
          error: 'Sessão expirada'
        }
      }

      return {
        isAuthenticated: true,
        cliente,
        loading: false,
        error: null
      }
      
    } catch (error) {
      console.error('❌ Erro ao verificar sessão:', error)
      this.logout() // Limpar sessão corrompida
      
      return {
        isAuthenticated: false,
        cliente: null,
        loading: false,
        error: 'Erro ao verificar sessão'
      }
    }
  }

  /**
   * 🚪 Logout
   * Remove a sessão salva
   */
  static logout(): void {
    localStorage.removeItem(this.STORAGE_KEY)
    console.log('🚪 Logout realizado')
  }

  /**
   * 💾 Salvar sessão no localStorage
   */
  static salvarSessao(cliente: Cliente): void {
    const sessao = {
      cliente,
      timestamp: Date.now()
    }
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessao))
  }

  /**
   * 🔄 Atualizar dados do cliente na sessão
   * Útil se os dados do cliente forem atualizados
   */
  static async atualizarSessao(): Promise<boolean> {
    try {
      const sessaoAtual = this.verificarSessao()
      
      if (!sessaoAtual.isAuthenticated || !sessaoAtual.cliente) {
        return false
      }

      const hubLogin = sessaoAtual.cliente.hub_login
      if (!hubLogin) {
        console.warn('⚠️ Sessão atual não possui hub_login; não é possível atualizar via magic link.')
        return false
      }

      const response = await fetch('/api/auth-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hub_login: hubLogin })
      })

      if (!response.ok) {
        return false
      }

      const body = await response.json().catch(() => null)
      const clienteAtualizado: Cliente | undefined = body?.cliente
      const sucesso = body?.ok === true && clienteAtualizado

      if (!sucesso || !clienteAtualizado) {
        return false
      }

      const clienteMesclado: Cliente = {
        ...sessaoAtual.cliente,
        ...clienteAtualizado,
      }

      this.salvarSessao(clienteMesclado)

      return true
      
    } catch (error) {
      console.error('❌ Erro ao atualizar sessão:', error)
      return false
    }
  }

}
